/**
 * Next.js 16 runs the App Router on its bundled React 19, even though the
 * Pages Router still uses our React 18. React 19 dropped two things that
 * react-bootstrap v1 (and the libraries under it) rely on:
 *
 * 1. `defaultProps` on function components. react-bootstrap sets important
 *    defaults that way: Modal's `dialogAs`, Dropdown.Item's and Nav.Link's
 *    `as`, Button's `variant`. Without them those components crash ("Element
 *    type is invalid ... got: undefined") or lose their styling.
 *    `restoreDefaultProps` puts them back by wrapping the forwardRef `render`.
 *
 * 2. `ReactDOM.findDOMNode`. react-transition-group (Modal's Fade, Navbar's
 *    Collapse), react-overlays (Dropdown) and react-input-mask call it, so opening a modal
 *    throws "findDOMNode is not a function". `restoreFindDOMNode` adds a
 *    small version back, only when React has none.
 *
 * Import the family module (modal, dropdown, nav, button) next to each
 * react-bootstrap import the App Router can reach. react-input-mask needs
 * `restoreFindDOMNode` too, so import inputMask next to it. Under React 18 both are
 * no-ops: props already arrive with their defaults, and findDOMNode exists.
 *
 * Delete this folder when react-bootstrap is upgraded to v2.
 */

import ReactDOM from 'react-dom'

type Props = Record<string, unknown>
type Render = ((props: Props, ref: unknown) => unknown) & {
  hasRestoredDefaults?: boolean
  displayName?: string
}
type ForwardRefWithDefaults = {
  render?: Render
  defaultProps?: Props
}

export const restoreDefaultProps = (...components: unknown[]): void => {
  components.forEach((component) => {
    const target = component as ForwardRefWithDefaults
    const { render, defaultProps } = target

    if (!render || !defaultProps || render.hasRestoredDefaults) {
      return
    }

    const withDefaults: Render = (props, ref) => {
      const merged = { ...props }
      Object.keys(defaultProps).forEach((key) => {
        if (merged[key] === undefined) {
          merged[key] = defaultProps[key]
        }
      })
      return render(merged, ref)
    }

    withDefaults.hasRestoredDefaults = true
    withDefaults.displayName = render.displayName || render.name

    target.render = withDefaults
  })
}

type Fiber = {
  stateNode?: unknown
  child?: Fiber | null
  sibling?: Fiber | null
}

const firstElement = (fiber?: Fiber | null): Node | null => {
  for (let current = fiber; current; current = current.sibling) {
    if (current.stateNode instanceof Node) {
      return current.stateNode
    }

    const found = firstElement(current.child)

    if (found) {
      return found
    }
  }

  return null
}

export const restoreFindDOMNode = (): void => {
  const dom = (ReactDOM as unknown) as {
    findDOMNode?: (instance: unknown) => Node | null
  }

  if (typeof dom.findDOMNode === 'function') {
    return
  }

  // The callers pass a class component instance (`this`). Return the first
  // DOM element it renders, like the old findDOMNode did.
  const findDOMNode = (instance: unknown): Node | null => {
    if (!instance) {
      return null
    }

    if (typeof Node !== 'undefined' && instance instanceof Node) {
      return instance
    }

    const fiber = (instance as { _reactInternals?: Fiber })._reactInternals

    return fiber ? firstElement(fiber.child) : null
  }

  try {
    dom.findDOMNode = findDOMNode
  } catch {
    // A frozen react-dom export (never seen so far) must not break the page.
  }
}
